import PropTypes from 'prop-types';
import Context from './context';
import { useAPI } from '../api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function ShapesAndStickersProvider({ children }) {
  const {
    actions: { getShapes, getStickers },
  } = useAPI();

  const [state, setState] = useState({
    customShapes: [],
    customStickers: [],
    isInitDataLoaded: false,
    isLoading: false,
  });
  const activeRequestRef = useRef();
  const isInitDataLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (isInitDataLoadedRef.current) {
      return;
    }

    const activeRequest = activeRequestRef.current;
    if (
      activeRequest?.getShapes === getShapes &&
      activeRequest?.getStickers === getStickers
    ) {
      return;
    }

    activeRequest?.abortController.abort();
    const abortController = new AbortController();
    const request = { abortController, getShapes, getStickers };
    activeRequestRef.current = request;
    setState((currentState) => ({ ...currentState, isLoading: true }));

    try {
      const [shapes, stickers] = await Promise.all([
        getShapes({ signal: abortController.signal }),
        getStickers({ signal: abortController.signal }),
      ]);

      if (
        abortController.signal.aborted ||
        activeRequestRef.current !== request
      ) {
        return;
      }

      isInitDataLoadedRef.current = true;
      setState((currentState) => ({
        ...currentState,
        customShapes: shapes.map((shape) => ({ ...shape, isCustom: true })),
        customStickers: stickers,
        isLoading: false,
        isInitDataLoaded: true,
      }));
    } catch (error) {
      if (
        !abortController.signal.aborted &&
        activeRequestRef.current === request
      ) {
        setState((currentState) => ({ ...currentState, isLoading: false }));
        console.error(error);
      }
    } finally {
      if (activeRequestRef.current === request) {
        activeRequestRef.current = null;
      }
    }
  }, [getShapes, getStickers]);

  useEffect(
    () => () => {
      activeRequestRef.current?.abortController.abort();
      activeRequestRef.current = null;
    },
    []
  );

  const onStickerError = useCallback((url) => {
    console.error(`Failed to load sticker from URL: ${url}`);
    setState((currentState) => ({
      ...currentState,
      customStickers: currentState.customStickers.filter(
        (sticker) => sticker.url !== url
      ),
    }));
  }, []);

  const context = useMemo(
    () => ({
      state,
      actions: { load, onStickerError },
    }),
    [load, onStickerError, state]
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

ShapesAndStickersProvider.propTypes = {
  children: PropTypes.node,
};

export default ShapesAndStickersProvider;
